import React from 'react'
import "../Styles/TermsConditions.css";

function TermsConditions() {
    return (
        <div className="term-condition-container">
            <div className="term-condition-header">
                <h1>Terms and Conditions</h1>
                <p className="term-condition-updated">Last Updated: March 6, 2025</p>
            </div>

            <div className="term-condition-section">
                <h2>1. Introduction</h2>
                <p>Welcome to UniUMS ("we," "our," or "us"). These Terms and Conditions ("Terms") govern your access to and use of the UniUMS platform, including our website at <a href="https://uniums.com">https://uniums.com</a>, our home portal at <a href="https://home.uniums.com">https://home.uniums.com</a>, and our mobile applications (collectively, the "Service").</p>
                <p>By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of the Terms, you may not access the Service.</p>
            </div>

            <div className="term-condition-section">
                <h2>2. Definitions</h2>
                <p className="term-condition-text">
                    <strong>Account:</strong> Your registered account on the UniUMS platform.<br />
                    <strong>Administrator:</strong> Users with administrative privileges who can create and manage Faculty and Student accounts.<br />
                    <strong>Content:</strong> Any information, data, text, documents, images, or other materials uploaded, downloaded or appearing on the Service.<br />
                    <strong>Faculty:</strong> Users designated as teaching staff or instructors within the platform.<br />
                    <strong>Student:</strong> Users registered as students within the platform.<br />
                    <strong>Subscription:</strong> The paid service plan that grants access to the UniUMS platform and its features.
                </p>
            </div>

            <div className="term-condition-section">
                <h2>3. Account Registration and User Types</h2>
                <p>The UniUMS platform supports multiple user types with different privileges:</p>
                <div className="term-condition-subsection">
                    <h3>3.1 Administrators</h3>
                    <p>Administrators can create and manage Faculty and Student accounts, oversee system settings, and have access to all platform modules. Administrators are responsible for ensuring proper user management and data security within their organization.</p>

                    <h3>3.2 Faculty</h3>
                    <p>Faculty members can access designated modules, share documents with students, manage course materials, and perform other teaching-related functions as permitted by their Administrator.</p>

                    <h3>3.3 Students</h3>
                    <p>Students can access learning materials, download files, submit assignments, and interact with Faculty members through the platform as permitted by their Administrator.</p>
                </div>
            </div>

            <div className="term-condition-section">
                <h2>4. Subscription and Payment</h2>
                <p>UniUMS is a subscription-based service. Access to the platform requires a valid subscription.</p>
                <div className="term-condition-subsection">
                    <h3>4.1 Subscription Plans</h3>
                    <p>We offer various subscription plans with different features and user limits. Details about our current plans and pricing are available on our website.</p>

                    <h3>4.2 Payment</h3>
                    <p>Payment must be made in accordance with the terms specified during the subscription process. All payments are non-refundable unless otherwise specified in our refund policy.</p>

                    <h3>4.3 Subscription Renewal</h3>
                    <p>Subscriptions automatically renew unless canceled before the renewal date. You can manage your subscription settings through your account dashboard.</p>
                </div>
            </div>

            <div className="term-condition-section">
                <h2>5. User Responsibilities</h2>
                <div className="term-condition-subsection">
                    <h3>5.1 Account Security</h3>
                    <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account or any other security breach.</p>

                    <h3>5.2 Acceptable Use</h3>
                    <p>You agree not to use the Service for any purpose that is illegal or prohibited by these Terms. You will not use the Service in any manner that could damage, disable, overburden, or impair the Service or interfere with any other party's use of the Service.</p>

                    <h3>5.3 Content Responsibility</h3>
                    <p>You are solely responsible for any Content that you upload, post, or otherwise make available through the Service. You represent and warrant that you have all rights necessary to upload such Content and that the Content does not violate any laws or third-party rights.</p>
                </div>
            </div>

            <div className="term-condition-section">
                <h2>6. Intellectual Property</h2>
                <div className="term-condition-subsection">
                    <h3>6.1 Our Intellectual Property</h3>
                    <p>The Service and its original content (excluding Content provided by users), features, and functionality are and will remain the exclusive property of UniUMS and its licensors. The Service is protected by copyright, trademark, and other laws of both the country and foreign countries.</p>

                    <h3>6.2 Your Content</h3>
                    <p>You retain all rights to your Content. By uploading Content to the Service, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, and display such Content in connection with providing the Service to you.</p>
                </div>
            </div>

            <div className="term-condition-section">
                <h2>7. Data Privacy</h2>
                <p>Your privacy is important to us. Our Privacy Policy, which is incorporated into these Terms, explains how we collect, use, and disclose information about you. By using the Service, you consent to the collection, use, and disclosure of information as described in our Privacy Policy.</p>
            </div>

            <div className="term-condition-section">
                <h2>8. Termination</h2>
                <p>We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including, without limitation, if you breach these Terms.</p>
                <p>Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service or contact us to request account deletion.</p>
            </div>

            <div className="term-condition-section">
                <h2>9. Limitation of Liability</h2>
                <p>In no event shall UniUMS, its directors, employees, partners, agents, suppliers, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.</p>
            </div>

            <div className="term-condition-section">
                <h2>10. Indemnification</h2>
                <p>You agree to defend, indemnify, and hold harmless UniUMS and its licensees and licensors, and their employees, contractors, agents, officers, and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees), resulting from or arising out of your use and access of the Service, or your violation of these Terms.</p>
            </div>

            <div className="term-condition-section">
                <h2>11. Changes to Terms</h2>
                <p>We reserve the right, at our sole discretion, to modify or replace these Terms at any time. We will provide notice of any changes by posting the new Terms on this page and updating the "Last Updated" date. Your continued use of the Service after any such changes constitutes your acceptance of the new Terms.</p>
            </div>

            <div className="term-condition-section">
                <h2>12. Contact Us</h2>
                <p>If you have any questions about these Terms, please contact us at:</p>
                <p className="term-condition-contact">
                    Email: uinums24hours@gmail.com<br />
                    Email: uinumsbhabani@gmail.com<br />
                    Email: uinumsmanas@gmail.com<br />
                    Email: bhabanishankarmohanta143@gmail.com<br />
                    Phone: +91 96926 15246<br />
                    Website: <a href="https://uniums.com">https://home.uniums.com</a>
                </p>
            </div>

            <div className="term-condition-footer">
                <p>© 2025 UniUMS. All rights reserved.</p>
            </div>
        </div>
    )
}

export default TermsConditions